// Single source of truth for client-side email cleaning/validation.
//
// This is a UX layer only - tradiepass-api is the source of truth and
// re-validates independently (RFC structure, uniqueness, verification state).
// Structural validation here defers entirely to `isemail` (already a
// dependency) rather than a hand-rolled character whitelist, so valid
// addresses using `+` aliases or other legitimate punctuation are never
// rejected. `allowUnicode: false` matches the API's RFC/ASCII-only check
// (egulias/email-validator), which otherwise silently accepts emoji/unicode
// in the local part by default.
import IsEmail from 'isemail'

const COMMON_DOMAIN_TYPOS = {
  'gmial.com': 'gmail.com',
  'gmali.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gnail.com': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'icloud.co': 'icloud.com',
}

/**
 * Trim an email and lowercase its domain, leaving the local part (before
 * the "@") untouched since it can be case-sensitive per RFC 5321 and `+`
 * aliasing relies on it staying as typed.
 *
 * @param {string} value
 * @returns {string}
 */
export function normalizeEmail(value) {
  const trimmed = (value || '').trim()
  const atIndex = trimmed.lastIndexOf('@')

  if (atIndex === -1) {
    return trimmed
  }

  const local = trimmed.slice(0, atIndex)
  const domain = trimmed.slice(atIndex + 1)

  return `${local}@${domain.toLowerCase()}`
}

/**
 * Structural validity only (RFC syntax, ASCII-only, length limits). Does
 * NOT check whether the account exists/is unique - that's the API's job.
 *
 * @param {string} value
 * @returns {boolean}
 */
export function isValidEmail(value) {
  const normalized = normalizeEmail(value)
  if (!normalized) {
    return false
  }
  return IsEmail.validate(normalized, { allowUnicode: false })
}

/**
 * Suggests a correction for a handful of common domain typos. Returns null
 * when there's nothing to suggest - callers should offer this as a
 * "did you mean...?" prompt, never apply it automatically.
 *
 * @param {string} value
 * @returns {string|null}
 */
export function suggestEmailCorrection(value) {
  const normalized = normalizeEmail(value)
  const atIndex = normalized.lastIndexOf('@')
  if (atIndex === -1) {
    return null
  }

  const local = normalized.slice(0, atIndex)
  const domain = normalized.slice(atIndex + 1)
  const suggestedDomain = COMMON_DOMAIN_TYPOS[domain]

  if (!suggestedDomain || suggestedDomain === domain) {
    return null
  }

  return `${local}@${suggestedDomain}`
}
